import { z } from 'zod'
import { ProjectType, ProjectStatus, MilestoneStatus, TaskStatus, TaskPriority } from '../types/enums'

export const CreateProjectSchema = z.object({
  name: z
    .string({ required_error: 'Proje adı zorunludur.' })
    .min(3, 'Proje adı en az 3 karakter olmalıdır.')
    .max(150),
  /** Boşsa sunucu PRJ-{yıl}-{sıra} üretir; kullanıcı isterse kendi kodunu verir */
  code: z
    .string()
    .min(3)
    .max(50)
    .regex(
      /^[A-Z0-9][A-Z0-9_-]*[A-Z0-9]$/,
      'Kod büyük harf, rakam, tire ve alt çizgi içerebilir. (ör. ACME-2026-DIJITAL)',
    )
    .optional(),
  type: z.nativeEnum(ProjectType, { required_error: 'Proje tipi zorunludur.' }),
  officeId: z.string({ required_error: 'Ofis seçimi zorunludur.' }).uuid(),
  pmId: z.string().uuid().optional(),
  customerId: z.string().uuid().nullable().optional(),
  proposalId: z.string().uuid().nullable().optional(),
  startDate: z.string({ required_error: 'Başlangıç tarihi zorunludur.' }).date(),
  endDate: z.string({ required_error: 'Bitiş tarihi zorunludur.' }).date(),
  /** Onaylı bütçe — string, backend'de Decimal'e çevrilir */
  budget: z
    .string({ required_error: 'Bütçe zorunludur.' })
    .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir bütçe değeri giriniz.'),
  currency: z.string().default('TRY'),
  /** KDV oranı (%) — boşsa firma varsayılanı (Ayarlar > Finans) */
  kdvRate: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Geçerli bir KDV oranı giriniz.').optional(),
  /** Bütçe/sözleşme bedeli KDV dahil mi girildi (false: KDV hariç) */
  budgetIncludesKdv: z.boolean().default(false),
  description: z.string().max(2000).nullable().optional(),
}).refine(
  (data) => new Date(data.endDate) > new Date(data.startDate),
  { message: 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır.', path: ['endDate'] },
)

/** PATCH /projects/:id/status gövdesi */
export const ChangeProjectStatusSchema = z.object({
  status: z.nativeEnum(ProjectStatus, { required_error: 'status alanı zorunludur.' }),
})

export const UpdateProjectSchema = z.object({
  name: z.string().min(3).max(150).optional(),
  type: z.nativeEnum(ProjectType).optional(),
  status: z.nativeEnum(ProjectStatus).optional(),
  officeId: z.string().uuid().optional(),
  pmId: z.string().uuid().nullable().optional(),
  customerId: z.string().uuid().nullable().optional(),
  startDate: z.string().date().optional(),
  endDate: z.string().date().optional(),
  budget: z.string().regex(/^\d+(\.\d{1,2})?$/).optional(),
  currency: z.string().optional(),
  kdvRate: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Geçerli bir KDV oranı giriniz.').optional(),
  budgetIncludesKdv: z.boolean().optional(),
  description: z.string().max(2000).nullable().optional(),
  weeklyStatusNote: z.string().max(500).nullable().optional(),
  completionPercentage: z.number().min(0).max(100).optional(),
  useBlockFloor: z.boolean().optional(),
})

export const AddProjectMemberSchema = z.object({
  userId: z.string({ required_error: 'Çalışan seçimi zorunludur.' }).uuid(),
  roleInProject: z
    .string({ required_error: 'Proje içi rol zorunludur.' })
    .min(2)
    .max(100),
  startDate: z.string().date().nullable().optional(),
  endDate: z.string().date().nullable().optional(),
  /** [IK-01] Haftalık hedef saat — maksimum firma ayarı ile kontrol edilir */
  weeklyTargetHours: z
    .number({ required_error: 'Haftalık hedef saat zorunludur.' })
    .min(1, 'Haftalık hedef saat en az 1 olmalıdır.')
    .max(80, 'Haftalık hedef saat 80\'i geçemez.'),
})

export const CreateMilestoneSchema = z.object({
  title: z.string({ required_error: 'Milestone başlığı zorunludur.' }).min(3).max(200),
  description: z.string().max(1000).nullable().optional(),
  startDate: z.string().date().nullable().optional(),
  dueDate: z.string({ required_error: 'Hedef tarih zorunludur.' }).date(),
  sortOrder: z.number().int().min(0).default(0),
})

export const UpdateMilestoneSchema = z.object({
  title: z.string().min(3).max(200).optional(),
  description: z.string().max(1000).nullable().optional(),
  startDate: z.string().date().nullable().optional(),
  status: z.nativeEnum(MilestoneStatus).optional(),
  dueDate: z.string().date().optional(),
  delayReason: z.string().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
})

export const CreateTaskSchema = z.object({
  title: z.string({ required_error: 'Görev başlığı zorunludur.' }).min(3).max(200),
  description: z.string().max(2000).nullable().optional(),
  milestoneId: z.string().uuid().nullable().optional(),
  progressItemId: z.string().uuid().nullable().optional(),
  assigneeId: z.string().uuid().nullable().optional(),
  priority: z.nativeEnum(TaskPriority).default(TaskPriority.MEDIUM),
  estimatedHours: z.number().min(0).nullable().optional(),
  dueDate: z.string().date().nullable().optional(),
})

export const UpdateTaskSchema = z.object({
  title: z.string().min(3).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  status: z.nativeEnum(TaskStatus).optional(),
  priority: z.nativeEnum(TaskPriority).optional(),
  assigneeId: z.string().uuid().nullable().optional(),
  milestoneId: z.string().uuid().nullable().optional(),
  progressItemId: z.string().uuid().nullable().optional(),
  estimatedHours: z.number().min(0).nullable().optional(),
  dueDate: z.string().date().nullable().optional(),
})

/** İlerleme grubu (aşama) ve kalemleri — gövdeler önceden şemasızdı; alanlar ve gevşeklik masaüstü arayüzüyle uyumludur */
export const CreateProgressGroupSchema = z.object({
  title: z.string({ required_error: 'title zorunludur.' }).trim().min(1, 'title zorunludur.').max(200),
  sortOrder: z.number().int().min(0).optional(),
  isDraft: z.boolean().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  items: z.array(z.object({ title: z.string().min(1).max(200), assigneeId: z.string().uuid().optional() })).optional(),
})
export const UpdateProgressGroupSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  sortOrder: z.number().int().min(0).optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
})
export const CreateProgressItemSchema = z.object({
  title: z.string({ required_error: 'title zorunludur.' }).trim().min(1, 'title zorunludur.').max(200),
  description: z.string().max(2000).optional(),
  assigneeId: z.string().uuid().optional(),
  sortOrder: z.number().int().min(0).optional(),
})
export const UpdateProgressItemSchema = z.object({
  completionPercentage: z.number().min(0).max(100).optional(),
  notes: z.string().max(2000).optional(),
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2000).optional(),
  assigneeId: z.string().uuid().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  progressMode: z.enum(['MANUAL', 'AUTO']).optional(),
})
export type CreateProgressGroupInput = z.infer<typeof CreateProgressGroupSchema>
export type UpdateProgressGroupInput = z.infer<typeof UpdateProgressGroupSchema>
export type CreateProgressItemInput = z.infer<typeof CreateProgressItemSchema>
export type UpdateProgressItemInput = z.infer<typeof UpdateProgressItemSchema>

/** Projeyi çoğaltır (`POST /projects/:id/duplicate`) — yeni proje kodu zorunlu */
export const DuplicateProjectSchema = z.object({
  code: z.string({ required_error: 'Yeni proje kodu zorunludur.' }).trim().min(1, 'Yeni proje kodu zorunludur.').max(50),
})
export type DuplicateProjectInput = z.infer<typeof DuplicateProjectSchema>
export type UpdateProjectMemoryInput = z.infer<typeof UpdateProjectMemorySchema>

export type CreateProjectInput = z.infer<typeof CreateProjectSchema>
/** Proje hafızası üst sınırı (karakter) — bağlam şişmesin diye kısa tutulur */
export const AI_PROJECT_MEMORY_MAX = 2000

/** Proje hafızasını (AI + kullanıcı notları) değiştirir — `PUT /projects/:id/memory`; boş metin hafızayı temizler */
export const UpdateProjectMemorySchema = z.object({
  memory: z.string().max(AI_PROJECT_MEMORY_MAX, `Hafıza en fazla ${AI_PROJECT_MEMORY_MAX} karakter olabilir.`),
  /** Düzenlemeye başlarken görülen sürüm (`aiMemoryUpdatedAt`); arada değiştiyse 409 döner. Yoksa/hiç hafıza yoksa null */
  baseVersion: z.string().datetime('Geçerli bir sürüm (ISO tarih) giriniz.').nullable().optional(),
})

export type UpdateProjectInput = z.infer<typeof UpdateProjectSchema>
export type AddProjectMemberInput = z.infer<typeof AddProjectMemberSchema>
export type CreateMilestoneInput = z.infer<typeof CreateMilestoneSchema>
export type UpdateMilestoneInput = z.infer<typeof UpdateMilestoneSchema>
export type CreateTaskInput = z.infer<typeof CreateTaskSchema>
export type UpdateTaskInput = z.infer<typeof UpdateTaskSchema>
